import {useState, useEffect, useRef} from 'react';
import {usePopoutWindow} from './usePopoutWindow';

// Types
// -------------------------
export interface PopoutDimensions {
  width: number;
  height: number;
  outerWidth: number;
  outerHeight: number;
  scrollX: number;
  scrollY: number;
  isInPopout: boolean;
  popoutId: string | null;
}
// ---------------

export interface UsePopoutDimensionsOptions {
  throttle?: number;
  trackScroll?: boolean;
  onResize?: (dimensions: PopoutDimensions) => void;
}

// ----------------------------
// usePopoutDimensions
// ----------------------------
export const usePopoutDimensions = (
  options: UsePopoutDimensionsOptions = {}
): PopoutDimensions => {
  const {throttle = 100, trackScroll = false, onResize} = options;
  const {popoutWindow, isInPopout, popoutId} = usePopoutWindow();
  const onResizeRef = useRef(onResize);

  useEffect(() => {
    onResizeRef.current = onResize;
  });

  // Initialize state with current dimensions
  const [dimensions, setDimensions] = useState<PopoutDimensions>({
    width: popoutWindow.innerWidth,
    height: popoutWindow.innerHeight,
    outerWidth: popoutWindow.outerWidth,
    outerHeight: popoutWindow.outerHeight,
    scrollX: popoutWindow.scrollX,
    scrollY: popoutWindow.scrollY,
    isInPopout,
    popoutId,
  });

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let lastUpdate = 0;

    const getDimensions = (): PopoutDimensions => ({
      width: popoutWindow.innerWidth,
      height: popoutWindow.innerHeight,
      outerWidth: popoutWindow.outerWidth,
      outerHeight: popoutWindow.outerHeight,
      scrollX: popoutWindow.scrollX,
      scrollY: popoutWindow.scrollY,
      isInPopout,
      popoutId,
    });

    const updateDimensions = () => {
      const newDimensions = getDimensions();
      setDimensions(newDimensions);
      onResizeRef.current?.(newDimensions);
    };

    const handleEvent = () => {
      const now = Date.now();

      if (throttle > 0) {
        // Throttle updates
        if (now - lastUpdate >= throttle) {
          lastUpdate = now;
          updateDimensions();
        } else {
          // Schedule update for end of throttle period
          if (timeoutId) clearTimeout(timeoutId);
          timeoutId = setTimeout(
            () => {
              lastUpdate = Date.now();
              updateDimensions();
            },
            throttle - (now - lastUpdate)
          );
        }
      } else {
        // No throttling
        updateDimensions();
      }
    };

    // Listen for resize events
    popoutWindow.addEventListener('resize', handleEvent);

    // Optionally listen for scroll events
    if (trackScroll) {
      popoutWindow.addEventListener('scroll', handleEvent);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      popoutWindow.removeEventListener('resize', handleEvent);
      if (trackScroll) {
        popoutWindow.removeEventListener('scroll', handleEvent);
      }
    };
  }, [popoutWindow, isInPopout, popoutId, throttle, trackScroll]);

  return dimensions;
};

export default usePopoutDimensions;
